/* Conversas: lista, detalhe, atribuição, etiquetas, status e o quadro. */
import {
  conversation,
  conversationBoard,
  paginated,
  renameContactInput,
  setConversationLabelsInput,
  setConversationStatusInput,
  startConversationInput,
  type Conversation,
  type ConversationBoard,
  type StartConversationInput,
} from "@zapir/shared";
import { z } from "zod";
import { request } from "./core";
import type { Page } from "./types";

/* Tipos derivados que o shared nao reexporta por nome. */
type SetConversationStatusInput = z.infer<typeof setConversationStatusInput>;

export const conversationsApi = {
  async conversations(params: {
    filtro: "todas" | "nao_lidas" | "minhas" | "fixadas" | "sem_resposta";
    etiqueta?: string;
    assignee?: string;
    status?: string;
    q?: string;
    cursor?: string;
    limit?: number;
  }): Promise<Page<Conversation>> {
    return request("/conversations", { query: { ...params }, schema: paginated(conversation) });
  },

  async conversation(id: string): Promise<Conversation> {
    return request(`/conversations/${id}`, { schema: conversation });
  },

  async startConversation(input: StartConversationInput): Promise<Conversation> {
    const body = startConversationInput.parse(input);
    return request("/conversations", { method: "POST", body, schema: conversation });
  },

  async setRead(conversationId: string, read: boolean): Promise<void> {
    await request(`/conversations/${conversationId}/read`, {
      method: "POST",
      body: { read },
    });
  },

  /** Fixar usa POST; desafixar e idempotente via DELETE (docs/07 RF-COL-077/081). */
  async setPinned(conversationId: string, pinned: boolean): Promise<void> {
    if (pinned) {
      await request(`/conversations/${conversationId}/pin`, { method: "POST", body: { pinned: true } });
    } else {
      await request(`/conversations/${conversationId}/pin`, { method: "DELETE" });
    }
  },

  async renameContact(conversationId: string, name: string): Promise<Conversation> {
    const body = renameContactInput.parse({ name });
    return request(`/conversations/${conversationId}/contact`, { method: "PATCH", body, schema: conversation });
  },

  async assign(
    conversationId: string,
    userId: string | null,
    expectedAssigneeId?: string | null,
  ): Promise<Conversation> {
    return request(`/conversations/${conversationId}/assign`, {
      method: "POST",
      body: { userId, expectedAssigneeId },
      schema: conversation,
    });
  },

  async setLabels(conversationId: string, labelIds: string[]): Promise<Conversation> {
    const parsed = setConversationLabelsInput.parse({ conversationId, labelIds });
    return request(`/conversations/${conversationId}/labels`, {
      method: "PUT",
      body: { labelIds: parsed.labelIds },
      schema: conversation,
    });
  },

  async setStatus(input: SetConversationStatusInput): Promise<Conversation> {
    const body = setConversationStatusInput.parse(input);
    return request(`/conversations/${body.conversationId}/status`, {
      method: "PATCH",
      body: { status: body.status, reason: body.reason },
      schema: conversation,
    });
  },

  async board(filtro?: string): Promise<ConversationBoard> {
    return request("/conversations/board", {
      query: { filtro },
      schema: conversationBoard,
    });
  },
};
